/**
 * Las reglas de un cuestionario: cuándo se puede jugar, cómo se heredan los ajustes y cuándo un
 * bloque de código no se leerá desde el fondo del aula.
 *
 * Todo es función pura (principio VIII) y todo se usa desde los dos lados: el editor para avisar
 * al profesor, y la importación de la fase 2 para rechazar un archivo que no se podría jugar.
 */
import { isQuestionTypeId } from './question-types/contract.js';
import { questionTypeOf } from './question-types/registry.js';
/**
 * Comprueba si un cuestionario está listo para jugarse.
 *
 * **Delega en el módulo de cada tipo**; no cuenta opciones a mano. Contarlas aquí funcionaría hoy,
 * con un solo tipo, y sería exactamente la clase de condicional por tipo que el principio VII
 * prohíbe: al añadir verdadero/falso habría que reescribir esta función.
 */
export function assessQuiz(questions) {
    if (questions.length === 0)
        return { playable: false, reason: 'noQuestions' };
    const issues = [];
    for (const question of questions) {
        const { id, position, type, ...config } = question;
        // Un tipo desconocido no es jugable, y el motivo que se muestra es el más útil: falta lo
        // básico. No se inventa un motivo nuevo para un caso que solo puede darse por corrupción.
        const problems = isQuestionTypeId(type)
            ? questionTypeOf(type).validateRaw(config)
            : [{ reason: 'emptyPrompt' }];
        if (problems.length > 0)
            issues.push({ questionId: id, position, problems });
    }
    if (issues.length > 0)
        return { playable: false, reason: 'invalidQuestions', issues };
    return { playable: true };
}
/**
 * Valores de la plataforma, el último eslabón de la cascada.
 *
 * Se anota con la interfaz en lugar de derivar los tipos con `as const`: si no, `time_limit_s`
 * tendría el tipo `20` en lugar de `number` y ningún otro valor encajaría.
 */
export const PLATFORM_SETTINGS = {
    time_limit_s: 20,
    shuffle_questions: true,
    shuffle_options: true,
    allow_alias: true,
    hints_enabled: true,
    auto_advance: false,
    auto_advance_delay_s: 8,
    points_multiplier: 1,
};
/**
 * Resuelve un ajuste: **el valor de la pregunta manda sobre el del cuestionario, y este sobre la
 * constante de la plataforma.** La primera que lo define gana.
 *
 * `null` y `undefined` significan «hereda», no «desactivado». Es la razón por la que esas
 * columnas admiten nulo en la base de datos: guardar el valor heredado en cada fila haría que
 * cambiar el ajuste del cuestionario no afectara a las preguntas que ya existen.
 */
export function resolveSetting(key, question, quiz) {
    const fromQuestion = question?.[key];
    if (fromQuestion !== undefined && fromQuestion !== null)
        return fromQuestion;
    const fromQuiz = quiz?.[key];
    if (fromQuiz !== undefined && fromQuiz !== null)
        return fromQuiz;
    return PLATFORM_SETTINGS[key];
}
// ---------------------------------------------------------------------------
// Ancho del código proyectado (RF-013)
// ---------------------------------------------------------------------------
/**
 * A partir de aquí, un bloque de código deja de leerse desde el fondo del aula.
 *
 * Sale de la geometría: con el tamaño mínimo de 28 px que exige §9.2 y una pantalla de proyector
 * corriente, caben unas setenta columnas. Es una estimación, no una medida: conviene comprobarla
 * con el proyector real antes de darla por buena.
 */
export const MAX_CODE_COLUMNS = 70;
/** Un tabulador ocupa lo que ocupa en pantalla, no un carácter. */
const TAB_COLUMNS = 4;
function columnsOf(line) {
    let columns = 0;
    // Se recorre por puntos de código: un emoji o una letra acentuada compuesta no debe contar
    // doble solo porque ocupe dos unidades de UTF-16.
    for (const char of line) {
        columns += char === '\t' ? TAB_COLUMNS - (columns % TAB_COLUMNS) : 1;
    }
    return columns;
}
/**
 * Encuentra las líneas de código demasiado largas.
 *
 * Se calcula sobre el **origen en Markdown**, no sobre el resultado renderizado: así funciona en
 * el editor mientras el profesor escribe y también en la validación de una importación, sin
 * necesitar un navegador.
 */
export function findLongCodeLines(markdown, max = MAX_CODE_COLUMNS) {
    const found = [];
    let fence = null;
    markdown.split('\n').forEach((raw, index) => {
        const opening = /^\s{0,3}(`{3,}|~{3,})/.exec(raw);
        if (fence === null) {
            // La cadena de información (```python) solo es válida al abrir.
            if (opening !== null)
                fence = opening[1] ?? null;
            return;
        }
        // Un cierre no lleva nada detrás y debe ser al menos tan largo como la apertura.
        const closing = /^\s{0,3}(`{3,}|~{3,})\s*$/.exec(raw);
        if (closing !== null && closing[1] !== undefined && closing[1][0] === fence[0]) {
            if (closing[1].length >= fence.length) {
                fence = null;
                return;
            }
        }
        const columns = columnsOf(raw);
        if (columns > max)
            found.push({ line: index + 1, columns });
    });
    return found;
}
//# sourceMappingURL=quiz-rules.js.map